/**
 * Utilities per la gestione responsive basate sui breakpoint di TailwindCSS
 *
 * Libreria pura con la logica responsive centralizzata, utilizzata sia dal mixin
 * che dal composable per evitare duplicazione di codice.
 *
 * Pattern identico a AuthUtils - logica condivisa e centralizzata.
 *
 * @example
 * // Uso diretto (utility functions)
 * import { ResponsiveUtils } from '@/lib/utils/responsiveUtils';
 *
 * const breakpoints = ResponsiveUtils.getBreakpoints();
 * const isLargeScreen = ResponsiveUtils.isBreakpointActive('lg', 1200);
 *
 * @author Sistema Timecard
 * @version 1.0.0
 */

import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '@/tailwind.config.js';

// Ottieni la configurazione completa di TailwindCSS (stesso approccio dell'originale)
const fullConfig = resolveConfig(tailwindConfig);
const screens = fullConfig.theme.screens;

/**
 * Configurazione dei breakpoint derivata da TailwindCSS
 */
export interface BreakpointConfig {
	/** Extra small screens (0px+) */
	xs: number;
	/** Small screens (640px+) */
	sm: number;
	/** Medium screens (768px+) */
	md: number;
	/** Large screens (1024px+) */
	lg: number;
	/** Extra large screens (1280px+) */
	xl: number;
	/** 2X Large screens (1536px+) */
	'2xl': number;
}

/**
 * Tipi per i breakpoint
 */
export type BreakpointKey = keyof BreakpointConfig;

/**
 * Classe utility statica per la gestione responsive
 *
 * Contiene tutta la logica responsive in un posto centralizzato,
 * seguendo lo stesso pattern di AuthUtils.
 */
export class ResponsiveUtils {
	/**
	 * Configurazione breakpoints da TailwindCSS (cache statica)
	 */
	private static _breakpoints: BreakpointConfig | null = null;

	/**
	 * Ottieni i breakpoint da TailwindCSS
	 */
	static getBreakpoints(): BreakpointConfig {
		if (!this._breakpoints) {
			this._breakpoints = {
				xs: 0,
				sm: parseInt(screens.sm), // 640px
				md: parseInt(screens.md), // 768px
				lg: parseInt(screens.lg), // 1024px
				xl: parseInt(screens.xl), // 1280px
				'2xl': parseInt(screens['2xl']), // 1536px
			};
		}
		return this._breakpoints;
	}

	/**
	 * Controlla se una larghezza è >= un breakpoint specifico
	 */
	static isBreakpointActiveFrom(breakpoint: BreakpointKey, width: number): boolean {
		const breakpoints = this.getBreakpoints();
		return width >= breakpoints[breakpoint];
	}

	/**
	 * Controlla se una larghezza è solo nel range di un breakpoint specifico
	 */
	static isBreakpointActiveOnly(breakpoint: BreakpointKey, width: number): boolean {
		const breakpoints = this.getBreakpoints();
		const current = breakpoints[breakpoint];

		// Per xs: < sm
		if (breakpoint === 'xs') {
			return width < breakpoints.sm;
		}

		// Per 2xl: >= 2xl
		if (breakpoint === '2xl') {
			return width >= current;
		}

		// Per altri: current <= width < next
		const keys: BreakpointKey[] = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
		const currentIndex = keys.indexOf(breakpoint);
		const nextBreakpoint = keys[currentIndex + 1];

		if (nextBreakpoint) {
			return width >= current && width < breakpoints[nextBreakpoint];
		}

		return width >= current;
	}

	/**
	 * Determina il breakpoint corrente basato sulla larghezza
	 */
	static getCurrentBreakpoint(width: number): BreakpointKey {
		const breakpoints = this.getBreakpoints();

		if (width >= breakpoints['2xl']) {
			return '2xl';
		}
		if (width >= breakpoints.xl) {
			return 'xl';
		}
		if (width >= breakpoints.lg) {
			return 'lg';
		}
		if (width >= breakpoints.md) {
			return 'md';
		}
		if (width >= breakpoints.sm) {
			return 'sm';
		}
		return 'xs';
	}

	/**
	 * Determina se è necessario forzare il re-render delle tabelle
	 * quando si attraversano breakpoint critici
	 */
	static shouldForceTableRerender(oldWidth: number, newWidth: number): boolean {
		const breakpoints = this.getBreakpoints();
		const criticalBreakpoints = [
			breakpoints.sm,
			breakpoints.md,
			breakpoints.lg,
			breakpoints.xl,
			breakpoints['2xl'],
		];

		return criticalBreakpoints.some(
			(bp) => (oldWidth < bp && newWidth >= bp) || (oldWidth >= bp && newWidth < bp),
		);
	}

	/**
	 * Ottieni il valore numerico di un breakpoint
	 */
	static getBreakpointValue(breakpoint: BreakpointKey): number {
		const breakpoints = this.getBreakpoints();
		return breakpoints[breakpoint];
	}

	/**
	 * Utility per ottenere tutte le computed properties responsive
	 * Usata sia dal mixin che dal composable
	 */
	static getResponsiveState(width: number) {
		return {
			// "Da breakpoint in su"
			showFromXs: this.isBreakpointActiveFrom('xs', width),
			showFromSm: this.isBreakpointActiveFrom('sm', width),
			showFromMd: this.isBreakpointActiveFrom('md', width),
			showFromLg: this.isBreakpointActiveFrom('lg', width),
			showFromXl: this.isBreakpointActiveFrom('xl', width),
			showFrom2xl: this.isBreakpointActiveFrom('2xl', width),

			// "Solo in questo range"
			showOnlyXs: this.isBreakpointActiveOnly('xs', width),
			showOnlySm: this.isBreakpointActiveOnly('sm', width),
			showOnlyMd: this.isBreakpointActiveOnly('md', width),
			showOnlyLg: this.isBreakpointActiveOnly('lg', width),
			showOnlyXl: this.isBreakpointActiveOnly('xl', width),

			// Breakpoint corrente
			currentBreakpoint: this.getCurrentBreakpoint(width),
		};
	}
}

/**
 * Funzioni di utilità esportate per convenience
 */
export const {
	getBreakpoints,
	isBreakpointActiveFrom,
	isBreakpointActiveOnly,
	getCurrentBreakpoint,
	shouldForceTableRerender,
	getBreakpointValue,
	getResponsiveState,
} = ResponsiveUtils;
